import { rueckfrage } from '../../components/Rueckfrage'
import { useTexte, WORTLAUT } from '../../texte'
import { useAufraeumen, useSettings, useSpeicher } from '../../hooks/queries'
import { groesse } from '../../lib/format'
import { ArchiveIcon, HiddenIcon, PhotoIcon, VideoIcon, WelleIcon } from '../../components/Icons'
import type { Verwaist } from '../../api/types'

/**
 * Wie voll es ist — und was davon an nichts mehr hängt.
 *
 * **Der Grund ist das Paket.** Auf geteiltem Webhosting ist der Speicher das
 * Kontingent, das zuerst ausgeht. `MEDIA_TOTAL_QUOTA_MB` bremst, bevor der
 * Webspace voll ist — aber eine Bremse, die niemand sieht, merkt man erst,
 * wenn sie greift. Aus dem Betrieb gewünscht: „Anzeige des Speicherplatzes,
 * nur für Admins."
 *
 * **Aufgeschlüsselt wird nur, was dem Betrachter gehört** — so gewünscht:
 * „jeder admin sieht das nur für seine Reisen". Die Summe der Anwendung steht
 * trotzdem da, denn sie beantwortet die Frage, um die es geht; alles Übrige
 * ist eine Zahl ohne Namen. Eine Summe verrät, wie voll es ist; ein Titel
 * verriete, was jemand geschrieben hat.
 *
 * **Der Knopf räumt genau das weg, was darüber gezählt wird.** Etwas anderes
 * zu löschen als das Angezeigte wäre eine Überraschung, und Überraschungen
 * beim Löschen sind die schlechteste Sorte.
 */
export function SpeicherCard() {
  const t = useTexte()
  const einstellungen = useSettings()
  // Ohne Decke ist dieses Konto keine Verwaltung -- der Server hat
  // entschieden, und danach richtet sich, ob überhaupt gefragt wird.
  const istVerwaltung = einstellungen.data?.ceiling !== undefined
  const speicher = useSpeicher(istVerwaltung)
  const raeumen = useAufraeumen()

  if (!istVerwaltung) return null

  const daten = speicher.data
  const voll = daten === undefined ? 0 : anteil(daten.used_bytes, daten.quota_bytes)

  return (
    <div className="card card--seite">
      <h2 className="card__titel">{t.speicher.titel}</h2>
      <p className="card__lead">{t.speicher.lead}</p>

      {speicher.isPending && <p className="feld__hinweis">{t.speicher.wirdGezaehlt}</p>}
      {speicher.isError && (
        <p className="notice" role="alert">
          {speicher.error instanceof Error
            ? speicher.error.message
            : t.speicher.nichtErmittelt}
        </p>
      )}

      {daten !== undefined && (
        <>
          {/* **Ein Balken und die Zahl daneben.** Der Balken beantwortet „wie
              eng wird es", die Zahl „wie viel genau" -- beides zusammen, weil
              die eine Frage ohne die andere selten gestellt wird. */}
          <div
            className="platz__balken"
            role="img"
            aria-label={t.speicher.belegt(Math.round(voll * 100))}
          >
            <span
              className={voll > 0.9 ? 'platz__voll platz__voll--eng' : 'platz__voll'}
              style={{ width: `${Math.min(100, voll * 100)}%` }}
            />
          </div>
          <p className="platz__zahl">
            <strong>{groesse(daten.used_bytes)}</strong> von{' '}
            {groesse(daten.quota_bytes)} belegt
            <span className="platz__leise"> · {daten.files} Dateien</span>
          </p>

          {/* Woraus die eigenen Reisen bestehen. Getrennt, weil sie sehr
              verschieden wiegen: ein verkleinertes Foto ist ein paar hundert
              Kilobyte, eine Minute 4K rund 150 MB. */}
          <div className="platz__arten">
            <Art
              zeichen={<PhotoIcon size={16} />}
              name={t.speicher.fotos}
              wert={daten.by_kind.image}
            />
            <Art
              zeichen={<VideoIcon size={16} />}
              name={t.speicher.videos}
              wert={daten.by_kind.video}
            />
            <Art
              zeichen={<WelleIcon size={16} />}
              name={t.speicher.sprache}
              wert={daten.by_kind.audio}
            />
          </div>

          {/* **Wie weit die Vorschaubilder gediehen sind** (#57).

              Aus dem Betrieb: „wie kann ich prüfen, dass das Vorschaubild da
              ist?" Von hier aus geht es, und zwar vom Telefon — die Dateizahl
              oben taugt dafür nicht: sie zählt Zeilen, und ein Vorschaubild
              bekommt keine eigene.

              **Ein Satz und keine Zeile der Tabelle darüber.** Dort steht, was
              etwas wiegt; hier, wie weit etwas gediehen ist. Ohne Fotos steht
              er gar nicht da — eine leere Auskunft ist keine. */}
          {daten.thumbs.total > 0 && (
            <p className="feld__hinweis" style={{ marginTop: 10 }}>
              {daten.thumbs.with >= daten.thumbs.total
                ? t.speicher.alleVorschaubilder
                : t.speicher.vorschaubilder(daten.thumbs.with, daten.thumbs.total)}
            </p>
          )}

          {/* **Nur die eigenen Reisen mit Namen.** Was anderen gehört, steht
              als eine Zahl darunter -- die Verwaltung sieht von fremden Reisen
              auch sonst nur Zahlen, nie einen Titel. */}
          {daten.trips.length > 0 && (
            <ul className="platz__reisen">
              {daten.trips.map((reise) => (
                <li key={reise.id} className="platz__reise">
                  <span className="platz__name">
                    {reise.title}
                    {reise.hidden && (
                      <HiddenIcon size={13} className="platz__marke" aria-label={t.speicher.zurueckgezogen} />
                    )}
                    {!reise.hidden && reise.archived && (
                      <ArchiveIcon size={13} className="platz__marke" aria-label={t.speicher.abgeschlossen} />
                    )}
                  </span>
                  <span className="platz__leise">{reise.files}</span>
                  <span className="platz__viel">{groesse(reise.bytes)}</span>
                  {/* Wie voll *diese* Reise ist -- das eigene Kontingent je
                      Reise steht in den Einstellungen darüber. */}
                  <span className="platz__mini" aria-hidden="true">
                    <span
                      style={{
                        width: `${Math.min(100, anteil(reise.bytes, daten.trip_quota_bytes) * 100)}%`,
                      }}
                    />
                  </span>
                </li>
              ))}
            </ul>
          )}

          {daten.others_bytes > 0 && (
            <p className="platz__zahl platz__leise">
              {t.speicher.reisenAnderer(groesse(daten.others_bytes))}
            </p>
          )}

          {/* **Liegengebliebenes.** Wer beim Schreiben abbricht, lässt Dateien
              zurück; sonst räumt sie der nächste Upload derselben Reise weg —
              was bei einer ruhenden Reise nie passiert. */}
          <p className="platz__zahl" style={{ marginTop: 12 }}>
            {daten.orphans.files === 0
              ? t.speicher.nichtsLiegt
              : t.speicher.liegt(daten.orphans.files, groesse(daten.orphans.bytes))}
          </p>

          {/* **Und was genau liegt da?** Aus dem Betrieb: „Anzeige der
              liegengebliebenen Dateien." Eine Zahl sagt, *dass* etwas liegt;
              wer darauf hin „wegräumen" drückt, löscht auf Verdacht. */}
          {daten.orphans.list.length > 0 && (
            <>
              <ul className="verwaist">
                {daten.orphans.list.map((datei) => (
                  <Liegengeblieben key={datei.id} datei={datei} />
                ))}
              </ul>
              {daten.orphans.files > daten.orphans.list.length && (
                <p className="feld__hinweis">
                  {t.speicher.weitere(daten.orphans.files - daten.orphans.list.length)}
                </p>
              )}
            </>
          )}

          {raeumen.isError && (
            <p className="notice" role="alert">
              {raeumen.error instanceof Error
                ? raeumen.error.message
                : t.allgemein.schiefgelaufen}
            </p>
          )}
          {raeumen.isSuccess && !raeumen.isPending && (
            <p className="feld__hinweis" role="status">
              {raeumen.data.files === 0
                ? t.speicher.nichtsWegzuraeumen
                : t.speicher.weggeraeumt(raeumen.data.files, groesse(raeumen.data.bytes))}
            </p>
          )}

          <button
            className="button button--outline"
            type="button"
            disabled={raeumen.isPending || daten.orphans.files === 0}
            onClick={async () => {
              if (
                await rueckfrage(
                  t.speicher.wegraeumenFrage(daten.orphans.files),
                  t.speicher.wegraeumenKurz,
                )
              ) {
                raeumen.mutate()
              }
            }}
          >
            {t.speicher.wegraeumen}
          </button>
        </>
      )}
    </div>
  )
}

/** Eine Zeile der Aufteilung — Zeichen, Name, Anzahl, Größe. */
function Art({
  zeichen,
  name,
  wert,
}: {
  zeichen: React.ReactNode
  name: string
  wert: { files: number; bytes: number }
}) {
  return (
    <p className="platz__art">
      <span className="platz__zeichen">{zeichen}</span>
      <span className="platz__name">{name}</span>
      {/* Die Null bleibt stehen statt zu verschwinden: „keine Videos" ist eine
          Auskunft, eine leere Stelle wäre keine. */}
      <span className="platz__leise">{wert.files}</span>
      <span className="platz__viel">{groesse(wert.bytes)}</span>
    </p>
  )
}

/** Anteil zwischen 0 und 1 — ohne Kontingent gibt es keinen. */
function anteil(teil: number, ganz: number): number {
  return ganz > 0 ? teil / ganz : 0
}

/**
 * Eine liegengebliebene Datei als Zeile — mit Vorschau, wo es eine geben darf.
 *
 * **Die Vorschau hängt am Server, nicht an dieser Seite.** Ein
 * Liegengebliebenes hängt an nichts und gehört deshalb nur dem, der es
 * hochgeladen hat; seit #59 können das auch Mitlesende sein. Fehlt die
 * Adresse, steht hier ein Platzhalter statt eines kaputten Bildes — und der
 * Satz daneben sagt, warum.
 *
 * **Ein Video zeigt sein Standbild und kein `<video>`.** Zwanzig Player in
 * einer Liste laden zwanzig Dateien an; hier geht es darum, etwas
 * wiederzuerkennen.
 */
function Liegengeblieben({ datei }: { datei: Verwaist }) {
  const t = useTexte()
  const bild = datei.thumb_url ?? (datei.kind === 'video' ? datei.poster_url : datei.url)

  return (
    <li className="verwaist__zeile">
      <span className="verwaist__bild">
        {bild !== null ? (
          <img src={bild} alt="" loading="lazy" />
        ) : (
          <span className="verwaist__ersatz" aria-hidden="true">
            {datei.kind === 'video' ? '▶' : datei.kind === 'audio' ? '♪' : '□'}
          </span>
        )}
      </span>
      <span className="verwaist__text">
        <span className="verwaist__reise">{datei.reise}</span>
        <span className="verwaist__daten">
          {artwort(datei)} · {groesse(datei.bytes)} ·{' '}
          {datei.tage === 0
            ? t.speicher.heute
            : datei.tage === 1
              ? t.speicher.seitEinemTag
              : t.speicher.seitTagen(datei.tage)}
          {!datei.mine && ` · ${t.speicher.vonMitlesendem}`}
        </span>
      </span>
    </li>
  )
}

/** „Foto", „GIF", „Video", „Sprachnachricht" — was jemand darunter versteht. */
function artwort(datei: Verwaist): string {
  // Ohne Komponente gibt es keinen Haken -- die dokumentierte Ausnahme.
  if (datei.kind === 'video') return WORTLAUT.speicher.artVideo
  if (datei.kind === 'audio') return WORTLAUT.speicher.artSprache
  return datei.mime === 'image/gif' ? WORTLAUT.speicher.artGif : WORTLAUT.speicher.artFoto
}
